// Expression-context type inference (§5.5, §5.8).
//
// `inferExprType` walks any `AstInput` node and returns the
// `ColumnType` it would evaluate to against a given dataset Schema.
// Mirror of `runtime/arquero.compileExpr`'s shape walk, but produces a
// type rather than a per-row function. Consumed by:
//
//   - pipeline verbs' `propagateSchema` (Phase 1.4) — e.g. `derive`'s
//     new columns get their types from `inferExprType` on the
//     right-hand expression
//   - the pre-evaluation type-check pass (Phase 1.5) — validates that
//     every expression-context sub-tree types cleanly before any I/O.

import { ShapeError, UnknownOpError } from '../errors.ts';
import { type AstInput, astShape, nodePos, opCallPos, type Spans } from '../normalize.ts';
import {
  type InferFn,
  type NameTable,
  opMeta,
  type PropagateSchemaCtx,
  resolveOpCall,
  type VerbMeta,
} from '../op-meta.ts';
import { elemPath } from '../path.ts';
import {
  arrayCol,
  booleanCol,
  type ColumnType,
  dateCol,
  integerCol,
  nullCol,
  numberCol,
  type Schema,
  stringCol,
  unknownCol,
} from './types.ts';

/**
 * Infer the `ColumnType` of an expression-context AST node.
 *
 *   literal → the leaf type (number→number|integer, string→string, …)
 *   vector → array<unified element type>
 *   symbol ref → schema.get(sym) ?? unknown
 *   op call → op.inferType(call, infer, path) ?? unknown
 *
 * For op calls, the registered op's `inferType` method (on
 * `EagerOpMeta`) is the single source of truth. Ops that don't declare
 * `inferType` resolve to `unknown` — downstream consumers (type-check
 * pass, derive's propagateSchema) treat `unknown` as a warning, not a
 * type error (§5.2).
 *
 * `Dataset` literals in expression context are unusual — a Dataset
 * isn't a column value — and resolve to `unknown`.
 */
export const inferExprType = (
  node: AstInput,
  schema: Schema,
  ops: NameTable,
  spans: Spans,
  path: string,
): ColumnType => {
  const shape = astShape(node);
  switch (shape.kind) {
    case 'primitive': {
      const v = shape.value;
      if (v === null) {
        return nullCol;
      }
      if (typeof v === 'number') {
        return Number.isInteger(v) ? integerCol : numberCol;
      }
      if (typeof v === 'string') {
        return stringCol;
      }
      if (typeof v === 'boolean') {
        return booleanCol;
      }
      return unknownCol;
    }
    case 'date':
      return dateCol;
    case 'dataset':
      return unknownCol;
    case 'vector': {
      const elementTypes = shape.elements.map((el, i) =>
        inferExprType(el as AstInput, schema, ops, spans, elemPath(path, i)),
      );
      return arrayCol(unifyColumnTypes(elementTypes));
    }
    case 'symbolRef':
      return schema.get(shape.sym) ?? unknownCol;
    case 'opCall': {
      const { call } = shape;
      const { meta } = resolveOpCall(call.name, ops, opMeta, opCallPos(spans, path), 'op', UnknownOpError);
      // Gate on the hook's presence, not `evaluation` — mirrors
      // `compileExpr`'s dispatch so a special form that compiles in
      // expression context (e.g. `(col …)`) also type-infers there.
      if (meta.inferType === undefined) {
        return unknownCol;
      }
      const infer: InferFn = (childNode, childPath) =>
        inferExprType(childNode as AstInput, schema, ops, spans, childPath);
      // `inferType`'s stored return widens to `unknown` for the same
      // `$replace` instantiation-depth reason `apply`'s does; cast back to
      // the declared `ColumnType` the op author annotated on the arrow.
      return meta.inferType(call, infer, path) as ColumnType;
    }
    case 'invalid':
      throw new ShapeError('expected an expression: literal, column reference, or op call', nodePos(spans, path));
  }
};

/**
 * Reduce a list of column types to a single common type. If every
 * element has the same `kind`, returns that type. Mixed-kind vectors
 * (e.g. `[1 'two' true]`) collapse to `unknown` — the engine does not
 * synthesise union types beyond the explicit `nullable` wrapper.
 *
 * Used by vector type inference for heterogeneous-element handling.
 */
export const unifyColumnTypes = (types: readonly ColumnType[]): ColumnType => {
  const head = types[0];
  if (head === undefined) {
    return unknownCol;
  }
  return types.every((t) => t.kind === head.kind) ? head : unknownCol;
};

/**
 * Run a verb's `propagateSchema` against an input schema, returning the
 * propagated `Schema` or `undefined` if the verb declares no propagator.
 * Centralises the `PropagateSchemaCtx` construction, the `InferFn` thunk
 * closing over `(inputSchema, ops, spans)`, and the `$output` / `Schema`
 * casts that paper over Zod's registry-stored signature widening — shared
 * between the runtime dispatcher (`evalVerbCall` in `evaluator.ts`) and
 * the pre-evaluation type-check pass (`handleTransformForm` in
 * `schema/check.ts`).
 *
 * The `parsed as never` cast matches the `$output` storage marker on
 * `BaseVerbMeta.propagateSchema`; the `as Schema` cast matches its
 * widened-to-`unknown` return. Both casts live here so the two call
 * sites don't each have to re-explain the ceremony.
 *
 * Throws are NOT caught here — runtime callers let them bubble into
 * `dispatchCall`'s `OpExecutionError` wrapping; the type-check pass
 * wraps its own call in try/catch to stay total.
 */
export const runPropagateSchema = (
  meta: VerbMeta,
  parsed: unknown,
  inputSchema: Schema,
  ops: NameTable,
  spans: Spans,
  path: string,
  liftColumn?: string,
): Schema | undefined => {
  if (meta.propagateSchema === undefined) {
    return undefined;
  }
  const ctx: PropagateSchemaCtx = {
    inputSchema,
    path,
    inferExprType: (node, p, schemaOverride) =>
      inferExprType(node as AstInput, schemaOverride ?? inputSchema, ops, spans, p),
    liftColumn,
  };
  return meta.propagateSchema(parsed as never, ctx) as Schema;
};
/**
 * Identity propagation: verbs that touch rows but never columns
 * (`filter`, `limit`, `distinct`, `sort`, …) share this
 * `propagateSchema` body. Lives in `schema/infer.ts` (rather than
 * `builtins.ts` where it originated) so verb files in
 * `ops/transform/` can import it without transitively importing
 * `builtins.ts` — that import cycle interleaved verb-module
 * registration against `builtins.ts`'s module-level initialisation
 * order, producing runtime `undefined` for verb-helper symbols.
 *
 * Single source of truth for "schema unchanged after this verb" —
 * one edit propagates to every consumer.
 */
export const passThroughSchema = (_data: unknown, ctx: PropagateSchemaCtx): Schema => ctx.inputSchema;
